import { useState, useCallback, memo, useEffect, useRef } from 'react'
import Button from 'shared/components/Button'
import Text from 'shared/components/Text'
import { colors } from 'shared/styles/colors'
import SnackBar from 'shared/components/SnackBar'

import { IEnviarPinTmovPay } from 'modules/pagamentos/domain/usecases/EnviarPinTmovPay'
import { IValidarPinTmovPay } from 'modules/pagamentos/domain/usecases/ValidarPinTmovPay'
import { useAuth } from 'modules/auth/hooks/auth'
import EModoEnvioPin from 'modules/pagamentos/domain/entities/EModoEnvioPin'
import Modal from 'shared/components/Modal'
import { SingleInputBoxes } from 'shared/components/InputBox/SingleInputBoxes'
import { Container } from '../../Pagamento/styles'
import {
  ContainerContent,
  ResendButton,
  ContainerInput,
  ContainerButton,
} from './styles'
import { EOrigemSolicitacaoPin } from 'modules/pagamentos/domain/entities/EOrigemEnvioPin'
import { useTransferencia } from 'modules/pagamentos/hooks/useTransferencia'

interface IPinProps {
  enviarPinTmovPay: IEnviarPinTmovPay
  validarPinTmovPay: IValidarPinTmovPay
  isOpened: boolean
  closeModal: () => void
  setOpenModalSenha(state: boolean): void
  origem: EOrigemSolicitacaoPin
}

const Pin = ({
  enviarPinTmovPay,
  validarPinTmovPay,
  isOpened,
  closeModal,
  setOpenModalSenha,
  origem,
}: IPinProps) => {
  const { pin, token } = useTransferencia()
  const [loading, setLoading] = useState(false)
  const { telefone } = useAuth()
  const [openSnack, setOpenSnack] = useState(false)
  const [loadingResend, setLoadingResend] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const validade = useRef(180)
  const [counter, setCounter] = useState(validade.current ?? 180)
  const tipoEnvio = useRef(EModoEnvioPin.SMS)

  useEffect(() => {
    const timer =
      counter > 0 && setInterval(() => setCounter(counter - 1), 1000)
    return () => clearInterval(timer)
  }, [counter])

  useEffect(() => {
    const enviarPin = async () => {
      setLoading(true)
      try {
        const response = await enviarPinTmovPay.execute({
          contato: telefone,
          tipoEnvio: EModoEnvioPin.SMS,
          origem,
        })

        token.current = response.token
        tipoEnvio.current = response.tipoReenvio
        validade.current = response.validade
        setCounter(validade.current)
        setLoadingResend(false)
      } catch (e: unknown) {
        if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Erro ao tentar enviar o pin')
        }
      } finally {
        setLoading(false)
      }
    }
    enviarPin()
  }, [])

  const handleSubmit = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')
    setOpenSnack(false)

    try {
      if (!pin) {
        setErrorMessage('Informe o código')
        setLoading(false)

        return
      }

      await validarPinTmovPay.execute({
        pin: pin.current,
        token: token.current,
        celular: telefone,
      })

      setLoading(false)
      closeModal()
      setOpenModalSenha(true)
    } catch (e: unknown) {
      setLoading(false)
      setErrorMessage(
        e instanceof Error ? e.message : 'Erro ao tentar validar o pin',
      )
      setOpenSnack(true)
    }
  }, [validarPinTmovPay])

  const handleResendCode = useCallback(async () => {
    setErrorMessage('')
    setLoadingResend(true)
    setCounter(validade.current)

    try {
      const response = await enviarPinTmovPay.execute({
        contato: telefone,
        tipoEnvio: tipoEnvio.current,
        origem,
      })
      token.current = response.token
      validade.current = response.validade
      setCounter(validade.current)

      setLoadingResend(false)
    } catch (e: unknown) {
      if (e instanceof Error) {
        setErrorMessage(e.message)
      } else {
        setErrorMessage('Erro ao tentar enviar o pin')
      }
      setLoadingResend(false)
    }
  }, [enviarPinTmovPay])

  const handleChange = (value: string) => {
    setErrorMessage('')
    pin.current = value
  }

  return (
    <>
      <Modal
        openModal={isOpened}
        showCloseButton={true}
        onCloseModal={closeModal}
      >
        <Container>
          <ContainerContent>
            <Text size={24} weight="700" style={{ paddingBottom: 8 }}>
              Verificação de celular
            </Text>

            <Text size={24} weight="400" color={colors.gray7}>
              Digite o código de 6 dígitos enviado por SMS para o telefone
              cadastrado.
            </Text>
            <ContainerInput>
              <SingleInputBoxes
                type="text"
                amount={6}
                onChangeValueInputBox={handleChange}
              />

              <ContainerButton>
                <Text size={14} weight="400" color={colors.gray7}>
                  Não recebeu o código?
                </Text>

                <ResendButton
                  transparent={true}
                  color={colors.grayLight}
                  disabled={counter > 0 || loadingResend}
                  loading={loadingResend}
                  type="button"
                  onClick={handleResendCode}
                >
                  Reenviar via Whatsapp
                </ResendButton>
                <Text size={14} weight="400" color={colors.gray7}>
                  {`(${Math.floor(counter / 60)}:${Math.floor(counter % 60)
                    .toString()
                    .padStart(2, '0')})`}
                </Text>
              </ContainerButton>
              <Button
                color={
                  pin.current.length === 6 ? colors.green : colors.grayLight
                }
                disabled={pin.current.length !== 6 || loading}
                loading={loading}
                type="submit"
                onClick={() => handleSubmit()}
              >
                Continuar
              </Button>
            </ContainerInput>
          </ContainerContent>
        </Container>
        <SnackBar
          open={openSnack}
          message={errorMessage}
          setOpen={setOpenSnack}
          severity={'error'}
        />
      </Modal>
    </>
  )
}

export default memo(Pin)
